import React, { useMemo } from 'react'
import { Bell } from 'lucide-react'
import classNames from 'classnames'

import type { AlertStatusType } from '@/api/types/Alert'
import type { NotificationType } from '@/api/types/Notification'
import { colorToRGB } from '@/utils/format'

import './Pill.scss'

type BaseStatusType = 'ACTIVE' | 'CONCLUDED'
type NotificationStatusType = 'DANGER' | 'WARNING' | 'SUCCESS'

interface PillBaseProps {
  children: React.ReactNode
  customColor?: string
}

type NormalPillProps = PillBaseProps & {
  variant?: 'normal' | 'neutral'
  status?: never
  notifications?: never
}

type StatusPillProps = PillBaseProps & {
  variant: 'status'
  status: BaseStatusType | AlertStatusType
  notifications?: never
}

type NotificationTypePillProps = PillBaseProps & {
  variant: 'notification-type'
  status: NotificationType
  notifications?: never
}

type NotificationPillProps = PillBaseProps & {
  variant: 'notification'
  status?: NotificationStatusType
  notifications: number
}

type PillProps = NormalPillProps | StatusPillProps | NotificationTypePillProps | NotificationPillProps

function Pill({
  children,
  variant = 'normal',
  status,
  notifications,
  customColor
}: PillProps) {

  const variantStatus = useMemo(() => {
    if (notifications !== undefined) {
      if (notifications < 5) return 'success'
      if (notifications < 10) return 'warning'
      return 'danger'
    }
    return status?.toLowerCase()
  }, [notifications, status])

  const colorStyles = useMemo(() => {
    if (customColor) return {
      color: customColor,
      backgroundColor: colorToRGB(customColor, 0.1)
    }
    return undefined
  }, [customColor])

  return (
    <div
      className={classNames('pill', variant, { [`${variant}-${variantStatus}`]: variantStatus })}
      style={colorStyles}
    >
      {variant === 'notification' && <Bell size={14} />}
      {children}
    </div>
  )
}

export default Pill
